/**
 * Os seis eixos de leitura do quadro de verificação (E78.F3).
 *
 * O quadro devolvido pelo motor é uma pilha de onze seções em que quase tudo
 * aparece com o mesmo peso: no caso real GT-2026-0005 o perito rolava sete
 * linhas de assinatura embutida para ler "não se aplica" antes de chegar ao
 * único item que de fato divergia. Este módulo não muda o quadro — ele só diz
 * **onde cada dado que o backend já devolveu deve ser lido** e **quanta
 * divergência há em cada eixo**, para que o eixo divergente abra primeiro e o
 * eixo em que tudo confere fique só no contador.
 *
 * A régua da divergência é a do próprio motor
 * (`services/exame_eletronico_identidade.py`): **divergência é contradição, não
 * falta de dado**. Só `nao_confere` conta. `ausente` e `nao_executado` são
 * ausências e limitações declaradas — elas continuam saindo por escrito no eixo
 * Alertas, e contá-las aqui inventaria um confronto que o exame não fez.
 */
import type {
  ItemVerificacaoData,
  ResultadoExameEletronicoData,
  SituacaoItem,
} from '@/lib/exame_eletronico';

export type ChaveEixo =
  | 'integridade'
  | 'identidade'
  | 'trilha'
  | 'rede'
  | 'localizacao'
  | 'alertas';

/** Ordem de leitura do quadro — e o desempate de qual eixo abre primeiro. */
export const ORDEM_DOS_EIXOS: readonly ChaveEixo[] = [
  'integridade',
  'identidade',
  'trilha',
  'rede',
  'localizacao',
  'alertas',
];

export interface EixoDoQuadro {
  chave: ChaveEixo;
  /** Itens que o exame apurou em CONTRADIÇÃO com o protocolo (`nao_confere`). */
  divergencias: number;
  /** Quantos itens o eixo examinou — é o que o contador mostra quando tudo confere. */
  examinados: number;
}

function contarDivergentes(itens: readonly ItemVerificacaoData[]): number {
  return itens.filter((item) => item.situacao === 'nao_confere').length;
}

/** Situação de um estágio externo pela chave; ausente quando o estágio não veio. */
function situacaoDoEstagio(
  enriquecimento: readonly ItemVerificacaoData[],
  chave: string,
): SituacaoItem | null {
  return enriquecimento.find((item) => item.chave === chave)?.situacao ?? null;
}

function divergentesEntreEstagios(
  enriquecimento: readonly ItemVerificacaoData[],
  chaves: readonly string[],
): number {
  return chaves.filter((chave) => situacaoDoEstagio(enriquecimento, chave) === 'nao_confere')
    .length;
}

/**
 * O eixo Integridade fecha com a assinatura embutida no arquivo.
 *
 * Só o veredito `nao_confirmada` é divergência: `nao_se_aplica` (o arquivo não
 * traz assinatura criptográfica) e `nao_foi_possivel_confirmar` são estados do
 * método, não contradição. Exame anterior ao estágio (`assinatura_digital`
 * nulo) não examinou nada — nem conta nem diverge.
 */
function assinaturaEmbutida(resultado: ResultadoExameEletronicoData): {
  divergencias: number;
  examinados: number;
} {
  if (!resultado.assinatura_digital) return { divergencias: 0, examinados: 0 };
  return {
    divergencias: resultado.assinatura_digital_veredito === 'nao_confirmada' ? 1 : 0,
    examinados: 1,
  };
}

/** Eventos que contradizem a trilha: fora de ordem ou com UTC e local incoerentes. */
function eventosDivergentes(resultado: ResultadoExameEletronicoData): number {
  return resultado.linha_do_tempo.filter(
    (evento) => evento.fora_de_ordem || evento.utc_local_incoerentes,
  ).length;
}

/** Reparte o quadro nos seis eixos, com o que cada um examinou e o que divergiu. */
export function eixosDoQuadro(resultado: ResultadoExameEletronicoData): EixoDoQuadro[] {
  const assinatura = assinaturaEmbutida(resultado);
  const ipsDivergentes = resultado.ips.filter((ip) => ip.situacao === 'nao_confere').length;
  return [
    {
      chave: 'integridade',
      divergencias: contarDivergentes(resultado.integridade) + assinatura.divergencias,
      examinados: resultado.integridade.length + assinatura.examinados,
    },
    {
      chave: 'identidade',
      divergencias: contarDivergentes(resultado.identidade),
      examinados: resultado.identidade.length,
    },
    {
      chave: 'trilha',
      divergencias: eventosDivergentes(resultado),
      examinados: resultado.linha_do_tempo.length,
    },
    {
      chave: 'rede',
      divergencias:
        ipsDivergentes + divergentesEntreEstagios(resultado.enriquecimento, ['classificacao_ip']),
      examinados: resultado.ips.length,
    },
    {
      chave: 'localizacao',
      divergencias:
        (resultado.coordenada_situacao === 'nao_confere' ? 1 : 0) +
        divergentesEntreEstagios(resultado.enriquecimento, ['cluster_cadastral', 'geocodificacao']),
      examinados: (resultado.coordenada === null ? 0 : 1) + resultado.cluster.length,
    },
    {
      chave: 'alertas',
      divergencias: resultado.red_flags.length,
      examinados: resultado.red_flags.length,
    },
  ];
}

/**
 * Qual eixo abre primeiro: o divergente de menor ordem de leitura.
 *
 * Sem divergência nenhuma, abre Integridade — o começo da leitura. Nunca abre
 * "nenhum": um quadro sem painel aberto obrigaria um clique para ver qualquer
 * coisa, que é o oposto do que a Fase veio resolver.
 */
export function eixoQueAbrePrimeiro(eixos: readonly EixoDoQuadro[]): ChaveEixo {
  return eixos.find((eixo) => eixo.divergencias > 0)?.chave ?? 'integridade';
}
